import React, { useState, useEffect } from 'react';
import Button from '@mui/material-next/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import PropTypes from "prop-types";
import { Autocomplete, Box, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
import Stack from '@mui/material/Stack';
import * as Yup from "yup";
import { Formik, Form } from 'formik'
import { get } from "lodash";
import CustomChip from '../../../widget/CustomChip';
import { ExpandMoreRounded, HighlightOffRounded } from '@material-ui/icons';
import BlurryDialog from '../../../widget/BlurryDialog';
import PromiseBuilder from "../../../utils/PromiseUtils";
import ApiClient from "../../../api/ApiClient";
import { useNotifications } from "../../../hooks/notification/notificationHook";
import { usersDtoToJ } from "../../../utils/CampaignMapper";

const CampaignDeleteCandidateDataDialog = (props) => {

  const { t } = useTranslation();
  const { addSuccessNotification, addErrorNotification } = useNotifications();

  const descriptionElementRef = React.useRef(null);
  const [allCandidates, setAllCandidates] = useState([]);

  const CampaignDeleteCandidateDataSchema = Yup.object().shape({
    candidates: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string(),
          label: Yup.string()
        })
      ).test({
        message: t('validation.required'),
        test: arr => arr.length > 0,
      })
  });

  useEffect(() => {
    if (props.open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [props.open]);

  useEffect(() => {
    fetchAllCandidateData();
  }, []);

  const fetchAllCandidateData = () => {
    props.setIsLoading(true);
    new PromiseBuilder()
      .then(async () => ApiClient.get("/api/v1/campaign/candidate"))
      .then(async (res) => {
        props.setIsLoading(false);
        const candidatesData = usersDtoToJ(res.data.candidates);
        setAllCandidates(candidatesData);
      })
      .catch(async (e) => {
        props.setIsLoading(false);
        addErrorNotification(t("toast.fetchCandidatesFailed"), t("toast.error"));
        setAllCandidates([]);
        console.log("ERROR", e);
      })
      .run();
  }

  const deleteCandidateData = (form, resetForm) => {
    let candidates = [];
    for (var i = 0; i < form.candidates.length; i++) {
      candidates.push({ email: form.candidates[i].label });
    }
    const payload = {
      candidates: candidates
    }
    props.setIsLoading(true);
    new PromiseBuilder()
      .then(async () => ApiClient.delete("/api/v1/campaign/candidate", payload))
      .then(async (res) => {
        props.setIsLoading(false);
        closeDialog(resetForm);
        addSuccessNotification(t('toast.candidatesDeleted'), t('toast.success'));
        props.handleClose();
        fetchAllCandidateData();
      })
      .catch(async (e) => {
        props.setIsLoading(false);
        addErrorNotification(t("toast.deleteCandidatesFailed"), t("toast.error"));
        setAllCandidates([]);
        console.log("ERROR", e);
      })
      .run();
  }

  const closeDialog = (resetForm) => {
    resetForm();
    props.handleClose();
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{ candidates: [] }}
      validationSchema={CampaignDeleteCandidateDataSchema}
      onSubmit={(values, { resetForm }) => {
        deleteCandidateData(values, resetForm);
      }}
    >
      {({ handleSubmit, resetForm, values, setFieldValue, errors, isValid, dirty }) => (
        <Form>
          <BlurryDialog
            open={props.open}
            onClose={() => closeDialog(resetForm)}
          >
            <DialogTitle
              id="scroll-dialog-title"
              className="color color-medium-red">
              <b>
                {t("candidates.deleteData")}
              </b>
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => closeDialog(resetForm)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent>
              <DialogContentText
                id="scroll-dialog-description"
                ref={descriptionElementRef}
                tabIndex={-1}

              >

                <Stack spacing={3}>
                  <div>
                    {t('candidates.dataWillBePermanentlyDeleted')}
                  </div>
                  <Autocomplete
                    multiple
                    id="dialog-delete-candidate-data"
                    value={get(values, "candidates")}
                    name={'candidates'}
                    onChange={(event, newValue) => {
                      setFieldValue('candidates', newValue)
                    }}
                    options={allCandidates}
                    getOptionLabel={(option) => option.label}
                    filterSelectedOptions
                    style={{ marginBottom: '15px' }}
                    renderOption={(props, option) => (
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div style={{ display: 'flex', flexDirection: 'row', fontWeight: '800' }}>
                            {option.name} {option.surname}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'row' }}>
                            {option.label}
                          </div>
                        </div>

                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px"
                          }
                        }}
                        variant="outlined"
                        size="small"
                        label={t("candidates.addCandidate")}
                        placeholder={t("candidates.addCandidate")}
                      />
                    )}
                    renderTags={(value, props) =>
                      value.map((option, index) => (
                        <CustomChip
                          id={index} key={index}
                          className="chip bg-dark-gray"
                          size="small"
                          color="#575760"
                          deleteIcon={
                            <HighlightOffRounded />
                          }
                          label={option.label} {...props({ index })}
                        />
                      ))
                    }
                    popupIcon={<ExpandMoreRounded />}
                  />
                </Stack>
              </DialogContentText>
            </DialogContent>
            <DialogContent>
              <DialogActions>
                <Button
                  variant="filled"
                  disableElevation
                  disabled={!isValid || !dirty}
                  className="bg bg-medium-red"
                  type="submit"
                  size="small"
                  style={{ borderRadius: '10px' }}
                  onClick={handleSubmit}
                >
                  {t("candidates.delete")}
                </Button>
              </DialogActions>
            </DialogContent>
          </BlurryDialog>
        </Form>
      )}
    </Formik>
  );
}

CampaignDeleteCandidateDataDialog.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleSave: PropTypes.func,
  setIsLoading: PropTypes.func
};

export default CampaignDeleteCandidateDataDialog;